import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { useHandler } from "@redotech/react-util/hook";
import { DatePicker } from "@redotech/redo-web/arbiter-components/date-picker/date-picker/date-picker-menu";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTimePicker,
  type TimeOfDay,
} from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";

interface SnoozeCustomModalProps {
  supportTicketId: SupportTicketId;
  onConfirm: (supportTicketId: SupportTicketId, snoozeUntil: Date) => void;
  onClose: () => void;
}

export const SnoozeCustomModal = memo(function SnoozeCustomModal({
  supportTicketId,
  onConfirm,
  onClose,
}: SnoozeCustomModalProps) {
  const today = Temporal.Now.plainDateISO();
  const [selectedDate, setSelectedDate] = useState<Temporal.PlainDate>(today);
  const [selectedTime, setSelectedTime] = useState<TimeOfDay>({
    hours: 9,
    minutes: 0,
  });

  const handleConfirm = useHandler(() => {
    const dateTime = new Date(
      selectedDate.year,
      selectedDate.month - 1,
      selectedDate.day,
      selectedTime.hours,
      selectedTime.minutes,
      0,
      0,
    );
    onConfirm(supportTicketId, dateTime);
    onClose();
  });

  const isInFuture = (() => {
    const dateTime = new Date(
      selectedDate.year,
      selectedDate.month - 1,
      selectedDate.day,
      selectedTime.hours,
      selectedTime.minutes,
    );
    return dateTime > new Date();
  })();

  return (
    <RedoModal
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Snooze",
        onClick: handleConfirm,
        disabled: !isInFuture ? "Snooze time must be in the future" : false,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Snooze until..."
    >
      <Flex dir="column" gap="lg">
        <DatePicker
          minDate={today}
          onDateChange={(date) => {
            if (date) {
              setSelectedDate(date);
            }
          }}
          selectedDate={selectedDate}
        />
        <Flex align="center" dir="row" gap="md">
          <Text fontSize="sm" textColor="secondary">
            Time
          </Text>
          <RedoTimePicker
            setValue={(v) => {
              if (v) {
                setSelectedTime(v);
              }
            }}
            value={selectedTime}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
